import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import type { Bank } from "../../components/Wallet/BankSelect";
import FleetrifyCheckbox from "../../components/Checkbox/checkbox";
import FleetrifyToggleSwitch from "../../components/Switch/ToggleSwitch";
import ProgressModal from "../../components/Wallet/ProgressModal";
import { FleetrifyModal } from "../../components/Modal/modal";
import BankDropdown from "../../components/Wallet/BankDropdown";
import {
  useGetBanksQuery,
  useVerifyAccountMutation,
  useTransferMutation,
  useCalculateTransferFeeMutation,
  useAddBeneficiaryMutation,
  useGetBeneficiariesQuery,
  useGetWalletQuery,
  TransferRequest,
  FeeBreakdown,
} from "../../redux/services/Wallet";
import { selectUser } from "../../redux/slices/authSlice";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";

type WithdrawFundsProps = {
  onCompleted: () => void;
};


const WithdrawFunds: React.FC<WithdrawFundsProps> = ({ onCompleted }) => {
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [isBeneficiariesModalOpen, setIsBeneficiariesModalOpen] =
    useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<string | null>(
    null
  );
  const [walletPin, setWalletPin] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [pinError, setPinError] = useState("");
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<"processing" | "completed" | "failed">(
    "processing"
  );
  const [message, setMessage] = useState("");
  const [feeBreakdown, setFeeBreakdown] = useState<FeeBreakdown | null>(null);
  const { data: banks = [], isLoading, isError } = useGetBanksQuery();
  const [verifyAccount, { isLoading: verifying, error: verifyError }] =
    useVerifyAccountMutation();
  const [transfer] = useTransferMutation();
  const [calculateTransferFee, { isLoading: isCalculatingFee }] =
    useCalculateTransferFeeMutation();
  const [addBeneficiary] = useAddBeneficiaryMutation();
  // Saved beneficiaries data
  const { data: beneficiariesData, isLoading: loadingBeneficiaries } =
    useGetBeneficiariesQuery();

  const savedBeneficiaries = beneficiariesData?.data ?? [];

  const user = useSelector(selectUser);
  const companyId = user?.company?.id || "";
  const { data: walletData } = useGetWalletQuery(companyId, {
    skip: !companyId,
  });
  const walletBalance = parseFloat(
    walletData?.data?.data?.wallet?.balance || "0"
  );

  // Live fee preview for the amount currently being entered
  const [feeQuote, setFeeQuote] = useState<FeeBreakdown | null>(null);
  const [feeQuoteError, setFeeQuoteError] = useState("");

  const [formData, setFormData] = useState<{
    amount: string;
    bank: Bank | null;
    accountNumber: string;
    receiversName: string;
    description: string; // ✅ added
  }>({
    amount: "",
    bank: null,
    accountNumber: "",
    receiversName: "",
    description: "", // ✅ default
  });

  const [errors, setErrors] = useState<{
    amount?: string;
    bank?: string;
    accountNumber?: string;
    description?: string; // ✅ added
  }>({});

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsCheckboxChecked(checked);
  };

  const handleToggleChange = (value: boolean) => {
    setIsToggleOn(value);
    console.log("Toggle is now:", value);
  };

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });

    if (errors[field as keyof typeof errors]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field as keyof typeof errors]: undefined,
      }));
    }
  };

  const resetWithdrawForm = () => {
    setFormData({
      amount: "",
      bank: null,
      accountNumber: "",
      receiversName: "",
      description: "",
    });

    setErrors({});
    setIsCheckboxChecked(false);
    setIsToggleOn(false);
    setWalletPin("");
    setPinError("");
    setProgress(0);
    setStatus("processing");
    setMessage("");
    setFeeBreakdown(null);
    setSelectedBeneficiary(null);
    setIdempotencyKey("");
  };


  const handleWithdrawClick = () => {
    // Open PIN confirmation modal first
    setIsPinModalOpen(true);
    setWalletPin("");
    setPinError("");
    // Generate one idempotency key for this withdrawal attempt so that
    // resubmits (e.g. a wrong-PIN retry) reuse the same key instead of
    // registering as separate transfers on the backend.
    setIdempotencyKey(
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : uuidv4()
    );
  };

  const handleSelectBeneficiary = () => {
    setIsBeneficiariesModalOpen(true);
  };

  const handleBeneficiarySelection = () => {
    if (selectedBeneficiary !== null) {
      const beneficiary = savedBeneficiaries.find(
        (b) => b.id === selectedBeneficiary
      );
      if (beneficiary) {
        setFormData({
          ...formData,
          bank: {
            name: beneficiary.bank_name,
            code: beneficiary.bank_code,
          } as Bank,
          accountNumber: beneficiary.account_number,
          receiversName: beneficiary.account_name,
        });
      }
      setIsBeneficiariesModalOpen(false);
    }
  };

  const handleConfirmWithdrawal = async () => {
    // Validate PIN
    if (!walletPin || walletPin.trim() === "") {
      setPinError("Please enter your wallet PIN");
      return;
    }

    if (walletPin.length < 4) {
      setPinError("PIN must be at least 4 digits");
      return;
    }

    // Close PIN modal and open progress modal
    setIsPinModalOpen(false);
    setIsProgressModalOpen(true);
    setProgress(0);
    setStatus("processing");

    try {
      const payload: TransferRequest = {
        amount: Number(formData.amount),
        account_number: formData.accountNumber,
        bank_code: formData.bank?.code || "",
        account_name: formData.receiversName,
        description: formData.description || "Withdrawal from wallet",
        transaction_pin: walletPin,
        idempotency_key: idempotencyKey,
      };

      const response = await transfer(payload).unwrap();
      console.log("✅ Transfer success:", response);

      // Animate progress 0 -> 100 smoothly
      let step = 0;
      const animation = setInterval(() => {
        step += 10;
        setProgress(step);
        if (step >= 100) {
          clearInterval(animation);

          if (response.status === "success") {
            setStatus("completed");
            setFeeBreakdown(response.data.result?.fee_breakdown ?? null);
            if (isToggleOn) {
              addBeneficiary({
                account_name: formData.receiversName,
                account_number: formData.accountNumber,
                bank_code: formData.bank?.code || "",
                bank_name: formData.bank?.name || "",
              })
                .unwrap()
                .then((res) => console.log("💾 Beneficiary saved:", res))
                .catch((err) =>
                  console.error("❌ Failed to save beneficiary:", err)
                );
            }
          } else {
            setStatus("failed");
            setMessage(response.data.message || "");
          }
        }
      }, 150); // every 150ms gives ~1.5s animation
    } catch (error: any) {
      console.error("❌ Transfer failed:", error);
      toast.error(error?.data?.error?.message || error?.data?.message || "Withdrawal failed. Please try again.");
      // Animate progress before showing failed
      let step = 0;
      const animation = setInterval(() => {
        step += 10;
        setProgress(step);
        if (step >= 100) {
          clearInterval(animation);
          setStatus("failed");
          setMessage(error?.data?.message || "");
        }
      }, 150);
    }
  };

  const handleBlur = (field: string) => {
    switch (field) {
      case "amount":
        if (formData.amount.trim() === "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            amount: "Amount is required.",
          }));
        } else if (parseFloat(formData.amount) < 100) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            amount: "Minimum withdrawal amount is 100.",
          }));
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, amount: undefined }));
        }
        break;

      case "bank":
        if (!formData.bank) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            bank: "Bank is required.",
          }));
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, bank: undefined }));
        }
        break;

      case "accountNumber":
        if (formData.accountNumber.trim() === "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            accountNumber: "Account number is required.",
          }));
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            accountNumber: undefined,
          }));
        }
        break;
      // case "description":
      //   if (formData.description.trim() === "") {
      //     setErrors((prev) => ({
      //       ...prev,
      //       description: "Description is required.",
      //     }));
      //   } else {
      //     setErrors((prev) => ({ ...prev, description: undefined }));
      //   }
      //   break;

      default:
        break;
    }
  };

  // Live fee preview: debounce calls to /wallet/transfer/fee as the user types an amount
  useEffect(() => {
    const amountValue = parseFloat(formData.amount);

    if (!formData.amount.trim() || isNaN(amountValue) || amountValue < 100) {
      setFeeQuote(null);
      setFeeQuoteError("");
      return;
    }

    const timer = setTimeout(() => {
      calculateTransferFee({ amount: amountValue })
        .unwrap()
        .then((res) => {
          setFeeQuote(res.data);
          setFeeQuoteError("");
        })
        .catch((err) => {
          console.error("❌ Fee calculation failed:", err);
          setFeeQuote(null);
          setFeeQuoteError("Unable to calculate the fee for this amount right now.");
        });
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.amount, calculateTransferFee]);

  const insufficientBalance = Boolean(
    walletData && feeQuote && feeQuote.total_debit > walletBalance
  );

  const isFormValid = () => {
    return (
      formData.amount.trim() !== "" &&
      parseFloat(formData.amount) >= 100 &&
      formData.bank !== null &&
      formData.accountNumber.trim() !== "" &&
      // formData.description.trim() !== "" &&
      isCheckboxChecked &&
      !insufficientBalance
    );
  };

  useEffect(() => {
    const bankCode = formData.bank?.code;
    const accountNumber = formData.accountNumber;

    if (bankCode && accountNumber && accountNumber.length === 10) {
      verifyAccount({ bankCode, accountNumber })
        .unwrap()
        .then((res) => {
          console.log("✅ Verified:", res);
          if (res?.data?.account_name) {
            setFormData((prev) => ({
              ...prev,
              receiversName: res.data.account_name,
            }));
          }
        })
        .catch((err) => {
          console.error("❌ Verification failed:", err);
          setFormData((prev) => ({
            ...prev,
            receiversName: "",
          }));
        });
    }
  }, [formData.bank?.code, formData.accountNumber, verifyAccount]);

  return (
    <div className="">
      <div className="mb-8">
        <h2 className="text-[16px] [16px]:text-[20px] font-semibold text-fleetblue mb-2">
          Withdrawal Form
        </h2>
        <p className="text-gray-500 text-[16px] mt-2 ">Enter desired amount</p>
      </div>
      <div>
        <label className="block text-[16px] font-medium text-fleetdark mb-2">
          Amount:<span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="amount"
          value={formData.amount}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
          placeholder="Enter amount"
          onChange={(e) => handleChange("amount", e.target.value)}
          onBlur={() => handleBlur("amount")}
        />
        {errors.amount && (
          <p className="text-red-500 text-sm">{errors.amount}</p>
        )}

        {formData.amount.trim() !== "" &&
          (feeQuote ? (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-100 rounded-lg text-sm space-y-1.5">
              <div className="flex justify-between font-semibold text-amber-900">
                <span>Total to be debited (incl. fees)</span>
                <span>₦{feeQuote.total_debit.toLocaleString()}</span>
              </div>
              {insufficientBalance && (
                <p className="text-red-600 font-medium pt-1">
                  Insufficient wallet balance for this withdrawal (including
                  fees).
                </p>
              )}
            </div>
          ) : (
            (isCalculatingFee || feeQuoteError) && (
              <div className="flex items-start gap-2 mt-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                <svg
                  className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-amber-700">
                  {isCalculatingFee ? "Calculating fee..." : feeQuoteError}
                </p>
              </div>
            )
          ))}
      </div>
      <div className="my-8">
        <h2 className="text-[16px] [16px]:text-[20px] font-semibold text-fleetblue mb-2">
          Account Details
        </h2>
        <p className="text-gray-500 text-[16px] mt-2 ">
          Enter withdrawal details
        </p>
      </div>
      <button
        onClick={handleSelectBeneficiary}
        className="border border-fleet  blue text-fleetblue px-6 py-2 rounded-lg hover:bg-blue-50 transition-colors"
      >
        Select from saved beneficiaries
      </button>

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <BankDropdown
          banks={banks}
          isLoading={isLoading}
          isError={isError}
          bankData={{
            bankName: formData.bank?.name || "",
            bankCode: formData.bank?.code,
          }}
          handleInputChange={(e) => {
            const { name, value } = e.target;
            if (name === "bankData") {
              setFormData({
                ...formData,
                bank: { name: value.bankName, code: value.bankCode } as Bank,
              });
            }
          }}
          handleBlur={() => handleBlur("bank")}
          errors={{ bankName: errors.bank }}
          clearError={
            (field) => setErrors((prev) => ({ ...prev, [field]: undefined })) // ✅ clears instantly
          }
        />

        <div>
          <label className="block text-[16px] font-medium text-fleetdark mb-2">
            Account Number:<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="accountNumber"
            value={formData.accountNumber}
            onChange={(e) => handleChange("accountNumber", e.target.value)}
            onBlur={() => handleBlur("accountNumber")}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
            placeholder="Enter account number"
          />
          {verifying && (
            <p className="text-blue-500 text-sm mt-1">Verifying account...</p>
          )}

          {verifyError && (
            <p className="text-red-500 text-sm mt-1">
              Failed to verify account. Please check details.
            </p>
          )}

          {errors.accountNumber && (
            <p className="text-red-500 text-sm mt-1">{errors.accountNumber}</p>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <div>
          <label className="block text-[16px] font-medium text-fleetdark mb-2">
            Reciever's Name:
          </label>
          <input
            type="text"
            name="receiversName"
            value={formData.receiversName}
            className="w-full px-4 py-3 border bg-gray-50  border-gray-300 rounded-lg outline-none"
            readOnly
          />
        </div>
        <div className="">
          <label className="block text-[16px] font-medium text-fleetdark mb-2">
            Description:
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            onBlur={() => handleBlur("description")}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none resize-none"
            placeholder="Enter a short note (e.g., Withdrawal for fuel expenses)"
            rows={3}
          />
          {errors.description && (
            <p className="text-red-500 text-sm mt-1">{errors.description}</p>
          )}
        </div>
      </div>

      <div className="my-4 flex items-center">
        <FleetrifyToggleSwitch
          value={isToggleOn}
          onChange={handleToggleChange}
          label="Saved Payment Methods"
        />
      </div>

      <div className="mb-6 flex items-center">
        <FleetrifyCheckbox
          label="I confirm the withdrawal details are correct."
          checked={isCheckboxChecked}
          onChange={handleCheckboxChange}
          size="medium"
        />
      </div>

      <div className="flex justify-end">
        <button
          className={`w-auto py-3 px-6 rounded-lg ${
            isFormValid()
              ? "bg-fleetblue text-white"
              : "bg-[#B4C0D8] text-[#D0D7E7]"
          }`}
          disabled={!isFormValid()}
          onClick={handleWithdrawClick}
        >
          save
        </button>
      </div>

      {/* PIN Confirmation Modal */}
      <FleetrifyModal
        isOpen={isPinModalOpen}
        onClose={() => setIsPinModalOpen(false)}
        title="Confirm Withdraw"
        className="max-w-2xl h-fit"
      >
        <div className="space-y-4 p-6">
          <h3 className="text-lg font-semibold text-gray-800">
            Enter Wallet PIN to Confirm
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wallet PIN
            </label>
            <input
              type="password"
              value={walletPin}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow numbers
                if (value === "" || /^\d+$/.test(value)) {
                  setWalletPin(value);
                  setPinError("");
                }
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-blue-500"
              placeholder="Enter PIN"
              maxLength={4}
              inputMode="numeric"
            />
            {pinError && (
              <p className="text-red-500 text-sm mt-1">{pinError}</p>
            )}
          </div>

          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
            <svg
              className="w-5 h-5 text-fleetblue mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-blue-700">
              Withdrawals take up to 24 hours for processing.
            </p>
          </div>

          <button
            onClick={handleConfirmWithdrawal}
            disabled={!walletPin || walletPin.length < 4}
            className={`w-auto py-3 px-6 rounded-lg transition-colors font-medium ${
              walletPin && walletPin.length >= 4
                ? "bg-fleetblue text-white "
                : "bg-[#B4C0D8] text-[#D0D7E7] cursor-not-allowed"
            }`}
          >
            Confirm Withdrawal
          </button>
        </div>
      </FleetrifyModal>

      {/* Progress Modal */}
      <ProgressModal
        isOpen={isProgressModalOpen}
        pinModal={setIsPinModalOpen}
        progress={progress}
        status={status}
        message={message}
        feeBreakdown={feeBreakdown}
        onClose={() => {
          setIsProgressModalOpen(false);

          if (status === "completed") {
            resetWithdrawForm();
            onCompleted(); // 👈 switch to Wallet Overview
          }
        }}
      />

      {/* Saved Beneficiaries Modal */}
      <FleetrifyModal
        isOpen={isBeneficiariesModalOpen}
        onClose={() => setIsBeneficiariesModalOpen(false)}
        title="Saved beneficiaries"
        className="max-w-md h-fit"
      >
        <div className="space-y-4 p-6">
          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-2">
              Withdrawal Options
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Select one of your saved accounts
            </p>

            {loadingBeneficiaries ? (
              <p className="text-gray-500 text-sm">
                Loading saved beneficiaries...
              </p>
            ) : savedBeneficiaries.length === 0 ? (
              <p className="text-gray-500 text-sm">
                No saved beneficiaries found.
              </p>
            ) : (
              <div className="space-y-3">
                {savedBeneficiaries.map((beneficiary: any) => (
                  <label
                    key={beneficiary.id}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="radio"
                      name="beneficiary"
                      value={beneficiary.id}
                      checked={selectedBeneficiary === beneficiary.id}
                      onChange={() => setSelectedBeneficiary(beneficiary.id)}
                      className="w-4 h-4 text-fleetblue"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-900">
                        {beneficiary.account_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {beneficiary.bank_name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {beneficiary.account_number}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={handleBeneficiarySelection}
            disabled={selectedBeneficiary === null}
            className={`w-auto py-3 px-6 rounded-lg transition-colors font-medium ${
              selectedBeneficiary !== null
                ? "bg-fleetblue text-white hover:bg-blue-700"
                : "bg-[#B4C0D8] text-[#D0D7E7] cursor-not-allowed"
            }`}
          >
            Select Account
          </button>
        </div>
      </FleetrifyModal>
    </div>
  );
};

export default WithdrawFunds;
